import { useEffect, useMemo, useRef, useState } from "react";
import {
  Trophy,
  Star,
  X,
  ArrowLeftRight,
  Loader2,
  Download,
} from "lucide-react";
import * as htmlToImage from "html-to-image";
import { toast } from "react-toastify";
import Layout from "@/components/Layout";
import TeamOfWeekCard from "@/pages/social-cards/TeamOfWeekCard";
import { autoEmbedImage } from "@/pages/social-cards/imageEmbed";
import {
  CARD_FORMATS,
  type CardFormat,
  type TeamOfWeekCardContent,
  type TeamOfWeekPlayerTile,
} from "@/pages/social-cards/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useGameweeks,
  useGameweekTotw,
  useGameweekEligiblePlayers,
  useTournamentTotw,
  useTournamentEligiblePlayers,
  TOTW_FORMATIONS,
  TOTW_PHASES,
  TOTW_PHASE_LABEL,
  type TotwFormation,
  type TotwPhase,
  type TotwSlot,
  type EligiblePlayer,
} from "@/hooks/api/useGameweeks";
import { cn } from "@/lib/utils";

const POSITION_ROWS: Array<{ key: TotwSlot["slot"]["position"]; label: string }> = [
  { key: "FWD", label: "Forwards" },
  { key: "MID", label: "Midfielders" },
  { key: "DEF", label: "Defenders" },
  { key: "GK", label: "Goalkeeper" },
];

/** Stable key for a slot — used as a map key for swap state. */
const slotKey = (s: TotwSlot["slot"]) => `${s.position}-${s.index}`;

function PlayerCard({
  p,
  onClick,
}: {
  p: TotwSlot;
  onClick?: () => void;
}) {
  const photo = p.player.photo;
  const initials = p.player.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const inner = (
    <div className="flex flex-col items-center w-24">
      <div className="relative">
        <div className="w-14 h-14 rounded-full bg-white shadow-md ring-2 ring-white/80 overflow-hidden flex items-center justify-center">
          {photo ? (
            <img src={photo} alt={p.player.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-slate-700 font-bold text-sm">{initials}</span>
          )}
        </div>
        {p.isCaptain && (
          <div
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 ring-2 ring-white flex items-center justify-center"
            title="Captain (highest points in the XI)"
          >
            <Star className="w-3 h-3 text-white fill-white" />
          </div>
        )}
        {p.player.club?.logoUrl && (
          <img
            src={p.player.club.logoUrl}
            alt={p.player.club.name}
            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full ring-1 ring-white bg-white object-contain"
          />
        )}
      </div>
      <div className="mt-1.5 text-[11px] font-semibold text-white text-center leading-tight truncate w-full">
        {p.player.name}
      </div>
      <div className="mt-0.5 px-1.5 py-0.5 rounded-md bg-amber-400 text-amber-950 text-[10px] font-bold">
        {p.stats.rawPoints} pts
      </div>
    </div>
  );

  if (!onClick) return inner;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Click to swap this player"
      className="group cursor-pointer hover:scale-105 transition-transform"
    >
      {inner}
    </button>
  );
}

function PitchRow({
  players,
  onSlotClick,
}: {
  players: TotwSlot[];
  onSlotClick?: (slot: TotwSlot["slot"]) => void;
}) {
  return (
    <div className="flex justify-evenly items-start py-2">
      {players.map((p) => (
        <PlayerCard
          key={slotKey(p.slot)}
          p={p}
          onClick={onSlotClick ? () => onSlotClick(p.slot) : undefined}
        />
      ))}
    </div>
  );
}

function Pitch({
  players,
  onSlotClick,
}: {
  players: TotwSlot[];
  onSlotClick?: (slot: TotwSlot["slot"]) => void;
}) {
  const byPos = useMemo(() => {
    const m: Record<TotwSlot["slot"]["position"], TotwSlot[]> = {
      GK: [],
      DEF: [],
      MID: [],
      FWD: [],
    };
    for (const p of players) m[p.slot.position].push(p);
    for (const k of Object.keys(m) as Array<keyof typeof m>) {
      m[k].sort((a, b) => a.slot.index - b.slot.index);
    }
    return m;
  }, [players]);

  return (
    <div
      className="relative rounded-2xl shadow-inner overflow-hidden p-4"
      style={{
        background:
          "linear-gradient(180deg, #0f7a3a 0%, #0b6630 50%, #0f7a3a 100%)",
        backgroundImage:
          "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 28px, transparent 28px 56px), linear-gradient(180deg, #0f7a3a 0%, #0b6630 50%, #0f7a3a 100%)",
      }}
    >
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="w-24 h-24 rounded-full border border-white/30" />
      </div>
      <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-px bg-white/30" />
      <div className="pointer-events-none absolute inset-3 border border-white/20 rounded-xl" />

      <div className="relative flex flex-col gap-3">
        {POSITION_ROWS.map((r) => (
          <PitchRow key={r.key} players={byPos[r.key]} onSlotClick={onSlotClick} />
        ))}
      </div>
    </div>
  );
}

/**
 * Modal-ish bottom sheet for swapping a player at a chosen slot.
 * Lists every eligible player at that position sorted by points,
 * with the current player marked.
 */
function SwapPanel({
  open,
  slot,
  currentPlayerId,
  eligible,
  alreadyInLineupIds,
  onSwap,
  onClose,
  isLoading,
}: {
  open: boolean;
  slot: TotwSlot["slot"] | null;
  currentPlayerId: number | null;
  eligible: EligiblePlayer[];
  alreadyInLineupIds: Set<number>;
  onSwap: (player: EligiblePlayer) => void;
  onClose: () => void;
  isLoading: boolean;
}) {
  if (!open || !slot) return null;
  const candidates = eligible.filter((e) => e.position === slot.position);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4 text-emerald-600" />
            <h3 className="text-base font-bold text-gray-900">
              Swap {slot.position} · slot {slot.index + 1}
            </h3>
            <Badge className="bg-emerald-100 text-emerald-700">
              {candidates.length} eligible
            </Badge>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-2">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading eligible players…
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-12">
              No eligible {slot.position} players from this gameweek's matches.
            </div>
          ) : (
            <div className="space-y-1">
              {candidates.map((c) => {
                const isCurrent = c.player.id === currentPlayerId;
                const isElsewhere =
                  !isCurrent && alreadyInLineupIds.has(c.player.id);
                return (
                  <button
                    key={c.player.id}
                    type="button"
                    disabled={isCurrent || isElsewhere}
                    onClick={() => onSwap(c)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2 rounded-lg text-left transition",
                      isCurrent
                        ? "bg-emerald-50 border border-emerald-300 cursor-default"
                        : isElsewhere
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-gray-50",
                    )}
                    title={
                      isCurrent
                        ? "Currently in this slot"
                        : isElsewhere
                          ? "Already in the lineup at another slot"
                          : "Click to swap in"
                    }
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 overflow-hidden shrink-0">
                      {c.player.photo ? (
                        <img
                          src={c.player.photo}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {c.player.name}
                        </span>
                        {isCurrent && (
                          <Badge className="bg-emerald-600 text-white text-[10px]">
                            On pitch
                          </Badge>
                        )}
                        {isElsewhere && (
                          <Badge className="bg-gray-200 text-gray-600 text-[10px]">
                            Already on pitch
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                        {c.player.club?.logoUrl && (
                          <img
                            src={c.player.club.logoUrl}
                            alt=""
                            className="w-3 h-3 object-contain"
                          />
                        )}
                        <span>{c.player.club?.name ?? "—"}</span>
                        <span className="text-gray-300">·</span>
                        {c.player.nation?.flagUrl && (
                          <img
                            src={c.player.nation.flagUrl}
                            alt=""
                            className="w-3 h-2 object-cover rounded-sm"
                          />
                        )}
                        <span>{c.player.nation?.name ?? "—"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-xs text-gray-500">
                      <span>{c.stats.goals}G</span>
                      <span>{c.stats.assists}A</span>
                      <span className="font-bold text-emerald-700 text-base">
                        {c.stats.rawPoints}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-3 border-t flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLOR: Record<string, string> = {
  UPCOMING: "bg-gray-200 text-gray-700",
  OPEN: "bg-green-100 text-green-700",
  LOCKED: "bg-orange-100 text-orange-700",
  COMPLETED: "bg-blue-100 text-blue-700",
};

/**
 * Which pool of matches the best XI is built from.
 * MATCHWEEK — one gameweek. TOURNAMENT — every match played, optionally
 * narrowed to a single phase.
 */
type TotwScope = "MATCHWEEK" | "TOURNAMENT";

export default function TeamOfTheWeekPage() {
  const [scope, setScope] = useState<TotwScope>("MATCHWEEK");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [phase, setPhase] = useState<TotwPhase>("ALL");
  const [formation, setFormation] = useState<TotwFormation>("4-4-2");

  const isTournament = scope === "TOURNAMENT";

  /** Local override lineup — initially seeded from server, mutated by admin swaps. */
  const [lineup, setLineup] = useState<TotwSlot[]>([]);
  const [swapSlot, setSwapSlot] = useState<TotwSlot["slot"] | null>(null);

  const { data: gameweeksData } = useGameweeks();
  const gameweeks = Array.isArray(gameweeksData) ? gameweeksData : [];

  useEffect(() => {
    if (selectedId !== null) return;
    if (!gameweeks.length) return;
    const sorted = [...gameweeks].sort((a: any, b: any) => b.id - a.id);
    const preferred = sorted.find(
      (g: any) => g.status === "COMPLETED" || g.status === "LOCKED",
    );
    setSelectedId((preferred ?? sorted[0]).id);
  }, [gameweeks, selectedId]);

  // Both scopes' queries are declared unconditionally (hook rules) and
  // gated by `enabled`, so switching scope doesn't refetch the other one.
  const gwQuery = useGameweekTotw(
    isTournament ? null : selectedId,
    formation,
  );
  const tournamentQuery = useTournamentTotw(formation, phase, isTournament);

  const gwEligible = useGameweekEligiblePlayers(
    isTournament ? null : selectedId,
  );
  const tournamentEligible = useTournamentEligiblePlayers(
    phase,
    isTournament,
  );

  // Normalise both responses to one shape the rest of the page reads from.
  const totw = isTournament ? tournamentQuery.data : gwQuery.data;
  const isLoading = isTournament
    ? tournamentQuery.isLoading
    : gwQuery.isLoading;
  const error = isTournament ? tournamentQuery.error : gwQuery.error;

  const eligibleFetching = isTournament
    ? tournamentEligible.isFetching
    : gwEligible.isFetching;
  const eligible =
    (isTournament ? tournamentEligible.data : gwEligible.data)?.players ?? [];

  /** Title shown on the card + used for the export filename. */
  const scopeTitle = isTournament
    ? phase === "ALL"
      ? "Team of the Tournament"
      : TOTW_PHASE_LABEL[phase]
    : ((totw as any)?.gameweek?.name ?? "Team of the Week");

  // Reset local lineup whenever the server-side TOTW (gameweek + formation) changes
  useEffect(() => {
    if (totw?.players) {
      setLineup(totw.players);
    }
  }, [totw?.players]);

  const alreadyInLineupIds = useMemo(
    () => new Set(lineup.map((p) => p.player.id)),
    [lineup],
  );

  const currentAtSlot = swapSlot
    ? lineup.find((p) => slotKey(p.slot) === slotKey(swapSlot))
    : null;

  const handleSwap = (incoming: EligiblePlayer) => {
    if (!swapSlot) return;
    const targetKey = slotKey(swapSlot);
    setLineup((prev) =>
      prev.map((p) => {
        if (slotKey(p.slot) !== targetKey) return p;
        return {
          slot: p.slot,
          isCaptain: false, // captaincy re-derived below
          player: incoming.player,
          stats: incoming.stats,
        };
      }),
    );
    setSwapSlot(null);
  };

  // Re-derive captain whenever lineup changes
  const lineupWithCaptain = useMemo(() => {
    if (lineup.length === 0) return lineup;
    let bestIdx = 0;
    for (let i = 1; i < lineup.length; i++) {
      const a = lineup[bestIdx].stats;
      const b = lineup[i].stats;
      if (
        b.rawPoints > a.rawPoints ||
        (b.rawPoints === a.rawPoints &&
          (b.goals > a.goals ||
            (b.goals === a.goals && b.assists > a.assists)))
      ) {
        bestIdx = i;
      }
    }
    return lineup.map((p, i) => ({ ...p, isCaptain: i === bestIdx }));
  }, [lineup]);

  const totalPoints = useMemo(
    () => lineupWithCaptain.reduce((s, p) => s + p.stats.rawPoints, 0),
    [lineupWithCaptain],
  );

  const isOverridden =
    totw?.players &&
    lineupWithCaptain.some(
      (p, i) => p.player.id !== totw.players[i]?.player.id,
    );

  const resetToAuto = () => {
    if (totw?.players) setLineup(totw.players);
  };

  // ─── Download as social card ──────────────────────────────────────────
  // Renders the same FBK-themed TeamOfWeekCard from /social-cards into a
  // hidden off-screen container at full export resolution, then captures
  // via html-to-image. Images are pre-embedded as data URLs to avoid the
  // canvas-taint issue when remote photo/crest/flag URLs lack CORS headers.

  const storyExportRef = useRef<HTMLDivElement>(null);
  const postExportRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState<CardFormat | null>(null);
  const [embeddedContent, setEmbeddedContent] =
    useState<TeamOfWeekCardContent | null>(null);

  const buildCardContent = async (): Promise<TeamOfWeekCardContent> => {
    /**
     * Try to inline `url` as a data URL. Returns null when:
     *   - url is empty / null / undefined
     *   - fetch errored (CORS, network, 404, …)
     *   - response wasn't actually an image (e.g. someone pasted a page URL
     *     into the photo field — server returns HTML, not a PNG)
     *
     * Dropping the URL to null lets the card render its initials / empty
     * fallback instead of carrying a broken URL into the export, which
     * would otherwise show up as `<img src="<bad-url>">` and crash
     * html-to-image on capture.
     */
    const embed = async (url: string | null | undefined) => {
      if (!url) return null;
      if (url.startsWith("data:")) return url;
      const r = await autoEmbedImage(url);
      if (r.success) return r.dataUrl;
      console.warn("[TOTW] dropping non-embeddable image:", url, r.reason);
      return null;
    };
    // Sort the same way the social card renderer does — FWD row first
    // (top of pitch), then MID, DEF, GK at the bottom — so the off-screen
    // render matches the pitch on this page.
    const order: Record<TeamOfWeekPlayerTile["position"], number> = {
      FWD: 0,
      MID: 1,
      DEF: 2,
      GK: 3,
    };
    const sorted = [...lineupWithCaptain].sort((a, b) => {
      const ap = order[a.player.position] ?? 9;
      const bp = order[b.player.position] ?? 9;
      if (ap !== bp) return ap - bp;
      return a.slot.index - b.slot.index;
    });
    const tiles: TeamOfWeekPlayerTile[] = await Promise.all(
      sorted.map(async (s): Promise<TeamOfWeekPlayerTile> => {
        const [photo, club, flag] = await Promise.all([
          embed(s.player.photo),
          embed(s.player.club?.logoUrl),
          embed(s.player.nation?.flagUrl),
        ]);
        return {
          name: s.player.name,
          position: s.player.position,
          photoUrl: photo,
          clubName: s.player.club?.name,
          clubLogoUrl: club,
          nationFlagUrl: flag,
          points: s.stats.rawPoints,
          isCaptain: s.isCaptain,
        };
      }),
    );
    return {
      gameweekName: scopeTitle,
      eyebrow: isTournament ? "TEAM OF THE TOURNAMENT" : "TEAM OF THE WEEK",
      formation,
      totalPoints,
      players: tiles,
      hashtags: isTournament
        ? "#WC2026 · #FBK · #TOTT"
        : "#WC2026 · #FBK · #TOTW",
      theme: "signature",
    };
  };

  const downloadCard = async (format: CardFormat) => {
    if (lineupWithCaptain.length === 0) {
      toast.warning(
        isTournament
          ? "No player stats found for this stage yet."
          : "Pick a gameweek with player stats first.",
      );
      return;
    }
    setDownloading(format);
    try {
      // Build + embed first, then wait one frame for React to render the
      // hidden card with the data URLs in place before capture.
      const content = await buildCardContent();
      setEmbeddedContent(content);
      await new Promise((r) => setTimeout(r, 650));

      const node =
        format === "story" ? storyExportRef.current : postExportRef.current;
      if (!node) {
        toast.error("Render target missing — refresh the page.");
        return;
      }
      const spec = CARD_FORMATS[format];
      const dataUrl = await htmlToImage.toPng(node, {
        width: spec.width,
        height: spec.height,
        pixelRatio: 2,
        cacheBust: true,
        style: { transform: "scale(1)", transformOrigin: "top left" },
      });
      const link = document.createElement("a");
      const slug = `${isTournament ? "tott" : "totw"}-${scopeTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")}-${formation}`;
      link.download = `fbk-${slug}-${format}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(`${CARD_FORMATS[format].label} downloaded`);
    } catch (err) {
      console.error(err);
      let msg: string;
      if (err instanceof Error) {
        msg = err.message;
      } else if (err && typeof err === "object" && "target" in err) {
        const src = (err as { target: { src?: string } }).target?.src;
        msg = src ? `image failed: ${src}` : "image failed to load during export";
      } else {
        msg = String(err);
      }
      toast.error(`Export failed: ${msg}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Trophy className="h-7 w-7 text-amber-500" />
            {isTournament ? "Team of the Tournament" : "Team of the Gameweek"}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {isTournament
              ? "Best XI across every match played, by total points. Click any player on the pitch to swap. Highest scorer in the XI is captain."
              : "Best XI for the selected gameweek. Click any player on the pitch to swap. Highest scorer in the XI is captain."}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Scope toggle — matchweek vs whole tournament */}
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
            {(
              [
                ["MATCHWEEK", "Matchweek"],
                ["TOURNAMENT", "Tournament"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setScope(value)}
                className={cn(
                  "px-3 py-1.5 rounded-md text-xs font-semibold transition-colors",
                  scope === value
                    ? "bg-amber-500 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100",
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="w-40">
            <Select
              value={formation}
              onValueChange={(v) => setFormation(v as TotwFormation)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Formation" />
              </SelectTrigger>
              <SelectContent>
                {TOTW_FORMATIONS.map((f) => (
                  <SelectItem key={f} value={f}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isTournament ? (
            <div className="w-56">
              <Select
                value={phase}
                onValueChange={(v) => setPhase(v as TotwPhase)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  {TOTW_PHASES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {TOTW_PHASE_LABEL[p]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="w-56">
              <Select
                value={selectedId ? String(selectedId) : ""}
                onValueChange={(v) => setSelectedId(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a gameweek" />
                </SelectTrigger>
                <SelectContent>
                  {gameweeks.map((g: any) => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm mb-4">
          Failed to load{" "}
          {isTournament ? "team of the tournament" : "team of the gameweek"}.
        </div>
      )}

      {isLoading && (
        <div className="rounded-2xl bg-slate-100 h-96 animate-pulse" />
      )}

      {!isLoading && totw && (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {isTournament ? (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                {TOTW_PHASE_LABEL[phase]}
                {(totw as any).scope
                  ? ` · ${(totw as any).scope.matchCount} matches · ${(totw as any).scope.playerCount} players`
                  : ""}
              </Badge>
            ) : (
              <Badge
                className={
                  STATUS_COLOR[(totw as any).gameweek?.status] ?? "bg-gray-100"
                }
              >
                {(totw as any).gameweek?.status}
              </Badge>
            )}
            <span className="text-sm text-gray-500">{formation}</span>
            {isOverridden && (
              <>
                <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                  Manually edited
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetToAuto}
                  className="text-xs h-7"
                >
                  Reset to auto-pick
                </Button>
              </>
            )}
            <span className="ml-auto text-sm text-gray-700">
              <span className="font-semibold">{totalPoints}</span> total pts ·{" "}
              <span className="font-semibold">{lineupWithCaptain.length}</span>/11 players
            </span>
          </div>

          {lineupWithCaptain.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
              No player stats stored yet for this gameweek. Log some match
              appearances and events first.
            </div>
          ) : (
            <>
              <Pitch
                players={lineupWithCaptain}
                onSlotClick={(s) => setSwapSlot(s)}
              />

              {/* Download as social card — same FBK pitch design from
                  /social-cards, exported at 2× resolution. Captures the
                  current formation + lineup (with any manual swaps). */}
              <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-bold text-emerald-900 uppercase tracking-wider flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Download as social card
                    </div>
                    <p className="text-[12px] text-emerald-800/80 mt-1">
                      The current formation + lineup (including manual swaps)
                      rendered with the FBK Team of the Week design. Story is
                      1080×1920 (Instagram / TikTok), Post is 1080×1350.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => downloadCard("story")}
                    disabled={downloading !== null}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {downloading === "story" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Story PNG (1080×1920)
                  </Button>
                  <Button
                    onClick={() => downloadCard("post")}
                    disabled={downloading !== null}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {downloading === "post" ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Post PNG (1080×1350)
                  </Button>
                </div>
                <p className="text-[10px] text-emerald-700/70 mt-2">
                  Exports at 2× resolution — Story → 2160×3840, Post → 2160×2700.
                  Player photos, club crests, and nation flags are auto-embedded
                  before capture so the export never tints from CORS.
                </p>
              </div>

              {/* Hidden off-screen render of the social card at full export
                  resolution. Only mounted when there's embedded content
                  ready — kept off-screen so the visible page layout stays
                  unaffected. */}
              {embeddedContent && (
                <div
                  aria-hidden="true"
                  style={{
                    position: "fixed",
                    left: -100000,
                    top: 0,
                    pointerEvents: "none",
                  }}
                >
                  <div ref={storyExportRef}>
                    <TeamOfWeekCard format="story" content={embeddedContent} />
                  </div>
                  <div ref={postExportRef}>
                    <TeamOfWeekCard format="post" content={embeddedContent} />
                  </div>
                </div>
              )}

              <div className="mt-6 rounded-xl border bg-white overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Player</TableHead>
                      <TableHead>Pos</TableHead>
                      <TableHead>Club</TableHead>
                      <TableHead>Nation</TableHead>
                      <TableHead className="text-center">Apps</TableHead>
                      <TableHead className="text-center">G</TableHead>
                      <TableHead className="text-center">A</TableHead>
                      <TableHead className="text-center">CS</TableHead>
                      <TableHead className="text-center">YC</TableHead>
                      <TableHead className="text-center">RC</TableHead>
                      <TableHead className="text-right">Pts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineupWithCaptain.map((p, i) => (
                      <TableRow key={slotKey(p.slot)}>
                        <TableCell className="text-gray-400">{i + 1}</TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {p.player.photo ? (
                              <img
                                src={p.player.photo}
                                alt={p.player.name}
                                className="w-7 h-7 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200" />
                            )}
                            <span>{p.player.name}</span>
                            {p.isCaptain && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                                C
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-slate-100 text-slate-700">
                            {p.slot.position}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {p.player.club?.logoUrl && (
                              <img
                                src={p.player.club.logoUrl}
                                alt=""
                                className="w-4 h-4 object-contain"
                              />
                            )}
                            <span className="text-sm">
                              {p.player.club?.name ?? "—"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {p.player.nation?.flagUrl && (
                              <img
                                src={p.player.nation.flagUrl}
                                alt=""
                                className="w-4 h-3 object-cover rounded-sm"
                              />
                            )}
                            <span className="text-sm">
                              {p.player.nation?.name ?? "—"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {p.stats.appearances}
                        </TableCell>
                        <TableCell className="text-center">
                          {p.stats.goals}
                        </TableCell>
                        <TableCell className="text-center">
                          {p.stats.assists}
                        </TableCell>
                        <TableCell className="text-center">
                          {p.stats.cleanSheets}
                        </TableCell>
                        <TableCell className="text-center">
                          {p.stats.yellowCards}
                        </TableCell>
                        <TableCell className="text-center">
                          {p.stats.redCards}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {p.stats.rawPoints}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </>
      )}

      <SwapPanel
        open={!!swapSlot}
        slot={swapSlot}
        currentPlayerId={currentAtSlot?.player.id ?? null}
        eligible={eligible}
        alreadyInLineupIds={alreadyInLineupIds}
        onSwap={handleSwap}
        onClose={() => setSwapSlot(null)}
        isLoading={eligibleFetching}
      />
    </Layout>
  );
}
